import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { isClientApiError } from "../../api/http.js";
import { useSession, type SessionUser } from "../../app/AppProviders.js";
import {
  CHANGE_PASSWORD_PATH,
  RETURN_PARAM,
  safeReturnPath,
  withReturnPath,
} from "../../app/RequireSession.js";
import { roleHomePath } from "../../app/RoleHomeRedirect.js";
import { describeError } from "../../ui/AsyncPanel.js";
import { Button, ControlSizeProvider } from "../../ui/Button.js";
import { TextField } from "../../ui/FormField.js";

/**
 * Employee sign-in. There is no public registration and no password reset
 * link: administrators issue and reset passwords. After sign-in the person
 * continues to the same-origin path they were heading for, or to their
 * role's home; a temporary password goes to the change-password page first.
 *
 * The group logo sits above the form as the page's identity: the opaque
 * white PNG in `public/brand` (the JPEG original keyed to transparent left
 * halos, so the card's surface stays pure white behind it), with its
 * intrinsic size on the element so the card never shifts while it loads.
 */

type LoginFieldErrors = { email?: string; password?: string };

/** Where a freshly signed-in person goes next. */
function destinationFor(user: SessionUser, next: string | null): string {
  if (user.mustChangePassword)
    return withReturnPath(CHANGE_PASSWORD_PATH, next);
  return next ?? roleHomePath(user.role);
}

function loginErrorMessage(error: unknown): string {
  if (isClientApiError(error)) {
    // The one 401 whose message is for the person: it names the deadline
    // the temporary password missed, and what to do about it.
    if (error.code === "TEMPORARY_PASSWORD_EXPIRED") return error.message;
    if (error.status === 401) {
      return "The email or password is incorrect. Check both and try again.";
    }
    if (error.status === 429) {
      return "Too many sign-in attempts. Wait 15 minutes, then try again.";
    }
  }
  return describeError(error).description;
}

function fieldErrorsFrom(error: unknown): LoginFieldErrors {
  if (!isClientApiError(error)) return {};
  const [email] = error.fieldErrors["email"] ?? [];
  const [password] = error.fieldErrors["password"] ?? [];
  return { email, password };
}

export function LoginPage() {
  const { user, status, login } = useSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const next = safeReturnPath(searchParams.get(RETURN_PARAM));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<LoginFieldErrors>({});

  const signIn = useMutation({
    mutationFn: login,
    onSuccess: (signedIn) => {
      navigate(destinationFor(signedIn, next), { replace: true });
    },
  });

  if (status === "authenticated" && user) {
    return <Navigate to={destinationFor(user, next)} replace />;
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const errors: LoginFieldErrors = {};
    if (!trimmedEmail) errors.email = "Enter your email.";
    if (!password) errors.password = "Enter your password.";
    setFieldErrors(errors);
    signIn.reset();
    if (errors.email || errors.password) return;
    signIn.mutate({ email: trimmedEmail, password });
  };

  const serverErrors = fieldErrorsFrom(signIn.error);
  const emailError = fieldErrors.email ?? serverErrors.email ?? null;
  const passwordError = fieldErrors.password ?? serverErrors.password ?? null;
  const showFormAlert =
    signIn.isError && !serverErrors.email && !serverErrors.password;

  return (
    <ControlSizeProvider size="touch">
      <main className="auth-page">
        <section className="auth-card" aria-labelledby="login-title">
          <header className="auth-card__header">
            <img
              className="auth-card__logo"
              src="/brand/chicagoland-auto-group.png"
              alt="Chicagoland Auto Group"
              width={535}
              height={141}
              decoding="async"
            />
            {/* The owner's wording for the sign-in page (2026-09-17): the full product name under the group logo. */}
            <p className="auth-card__brand">Chicagoland Auto Tools</p>
            <h1 id="login-title" className="auth-card__title">
              Sign in
            </h1>
            <p className="auth-card__lead">
              Use the employee account an administrator set up for you.
            </p>
          </header>

          <form className="auth-form" onSubmit={onSubmit} noValidate>
            {showFormAlert ? (
              <div role="alert" className="form-alert">
                {loginErrorMessage(signIn.error)}
              </div>
            ) : null}

            <TextField
              id="login-email"
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={emailError}
              autoFocus
            />

            <TextField
              id="login-password"
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={passwordError}
            />

            <div className="auth-form__actions">
              <Button
                type="submit"
                variant="primary"
                busy={signIn.isPending}
                className="auth-form__submit"
              >
                Sign in
              </Button>
            </div>
          </form>

          <p className="auth-card__footnote">
            Forgot your password? Ask an administrator to issue a temporary one.
          </p>
        </section>
      </main>
    </ControlSizeProvider>
  );
}
